import React, { useRef, useState } from 'react';
import { Group } from 'react-konva';
import { DisplayModes, GetDisplayMode } from 'utils/Global';
import ScoreView from 'components/plateviewer/render/plate/plateviewer/designview/ScoreView';
import AnnotationLoader from 'components/plateviewer/plate/AnnotationLoader';
import PlateIngredientRenderer from './plateviewer/designview/PlateIngredientRenderer';
import AnnotationView from 'components/plateviewer/plate/AnnotationView';
import {
  AnnotationLoaderDetailsProps,
  PlateRendererProps,
} from 'business/models/CustomModels';
import { AnnotationsDTO } from 'business/models/Models';
import { DisplayMode } from 'components/Experiment/constants';
import { useSelectedDropImageParameters } from 'redux/hooks/experiment';

function PlateRenderer(props: PlateRendererProps) {
  const { isScreen, isWellSelectionMode, plate, shouldShowIngredients } = props;
  const ingGroupRef = useRef();
  const imgScrGroupRef = useRef();
  const [drawResponse, setDrawResponse] = useState<AnnotationsDTO>(new AnnotationsDTO());
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { dropNumber } = selectedDropImageParameters || {};

  function isWellIngredientVisible() {
    return GetDisplayMode() === DisplayModes.WELLINGREDIENT;
  }

  function isAnyScoreViewVisible() {
    return (
      GetDisplayMode() === DisplayModes.SCORE ||
      GetDisplayMode() === DisplayModes.IMAGESCORE ||
      GetDisplayMode() === DisplayModes.IMAGE
    );
  }
  function isScoreVisible() {
    return GetDisplayMode() === DisplayModes.SCORE;
  }

  function getAnnotationRenderer({
    plate,
    scores,
    wellNumsContainingScribbles,
    scribbles,
    annotations,
    roi,
    disabledImages,
    histogramImages,
  }: AnnotationLoaderDetailsProps) {
    if (!isScoreVisible()) {
      if (isWellIngredientVisible()) {
        return (
          <AnnotationView
            plate={plate}
            scores={scores}
            wellNumsContainingScribbles={[]}
            scribbles={[]}
            annotations={[]}
            roi={[]}
            disabledImages={[]}
            histogramImages={[]}
            dropNumber={dropNumber}
          />
        );
      }
      return (
        <AnnotationView
          plate={plate}
          scores={scores}
          wellNumsContainingScribbles={wellNumsContainingScribbles}
          scribbles={scribbles}
          annotations={annotations}
          roi={roi}
          disabledImages={disabledImages}
          histogramImages={histogramImages}
          dropNumber={dropNumber}
        />
      );
    }
    if (isScoreVisible()) {
      return <ScoreView scoresDetail={scores} plate={plate} />;
    }
    return null;
  }

  const renderPlateIngredient = (_displayMode: DisplayMode, id: string) => {
    return (
      <Group id={id} visible={GetDisplayMode() === _displayMode} ref={ingGroupRef}>
        <PlateIngredientRenderer
          ingredientsWells={plate?.ingredients?.wellList}
          containerDropList={plate?.ingredients?.containerDropList}
          maxWellVolume={plate?.ingredients?.maxWellVolume}
          grpRef={ingGroupRef.current}
          canvasRefs={props.canvasRefs}
          displayMode={_displayMode}
          dropNumber={dropNumber}
          rows={plate?.rows}
          cols={plate?.cols}
          height={plate?.height}
          wellRects={plate?.wellRects}
          wellRectsPadded={plate?.wellRectsPadded}
          width={plate?.width}
          ingredients={plate?.ingredients}
          plateNumber={plate.plateNumber}
          platePosition={{ colPos: plate.colPos, rowPos: plate.rowPos }}
          shouldShowIngredients={shouldShowIngredients}
          strokeNormalizer={props.strokeNormalizer}
          wellScores={plate?.plateScores?.flat()}
        />
      </Group>
    );
  };

  if (!plate) {
    return null;
  }

  if (isScreen) {
    return renderPlateIngredient(GetDisplayMode() as DisplayMode, 'screen-group');
  }

  return (
    <>
      {GetDisplayMode() === DisplayMode.DESIGN &&
        renderPlateIngredient(DisplayMode.DESIGN, 'konva-group-design')}
      {GetDisplayMode() === DisplayMode.WELLINGREDIENT &&
        renderPlateIngredient(DisplayMode.WELLINGREDIENT, 'konva-group-plate-ingredient')}
      <Group
        id="plate-annotation-group"
        listening={!isWellSelectionMode}
        visible={isAnyScoreViewVisible() || isWellIngredientVisible()}
        ref={imgScrGroupRef}
      >
        <AnnotationLoader
          {...props}
          key={plate.plateId}
          plate={plate}
          response={drawResponse}
          setResponse={setDrawResponse}
          render={({
            plate,
            scores,
            wellNumsContainingScribbles,
            scribbles,
            annotations,
            roi,
            disabledImages,
            histogramImages,
            dropNumber,
          }: AnnotationLoaderDetailsProps) =>
            getAnnotationRenderer({
              plate,
              scores,
              wellNumsContainingScribbles,
              scribbles,
              annotations,
              roi,
              disabledImages,
              histogramImages,
              dropNumber,
            })
          }
        />
      </Group>
    </>
  );
}

export default PlateRenderer;
